import React, { useState } from 'react';
import {
  Table,
  Card,
  Tag,
  Space,
  Button,
  Input,
  Select,
  Typography,
  Empty,
  Alert,
  Tooltip,
  Grid,
} from 'antd';
import {
  ReloadOutlined,
  SearchOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ClockCircleOutlined,
  SyncOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { usePayments } from '../hooks/usePayments';
import { Payment, PaymentStatus } from '../types/payment.types';

const { Text, Title } = Typography;
const { useBreakpoint } = Grid;

// Status configuration
const statusConfig: Record<PaymentStatus, { color: string; icon: React.ReactNode; text: string }> = {
  success: {
    color: 'success',
    icon: <CheckCircleOutlined />,
    text: 'Успешно',
  },
  failed: {
    color: 'error',
    icon: <CloseCircleOutlined />,
    text: 'Ошибка',
  },
  pending: {
    color: 'warning',
    icon: <ClockCircleOutlined />,
    text: 'Ожидание',
  },
  processing: {
    color: 'processing',
    icon: <SyncOutlined spin />,
    text: 'Обработка',
  },
};

// Format date - handle ISO format dates
const formatDate = (dateString: string | null | undefined): string => {
  if (!dateString) return '-';

  // Parse ISO date string (server sends UTC time with Z)
  const date = new Date(dateString);

  // Check if date is valid
  if (isNaN(date.getTime())) return '-';

  // Display in local timezone
  return date.toLocaleString('ru-RU', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

// Format amount
const formatAmount = (amount: number): string => {
  return new Intl.NumberFormat('ru-RU', {
    style: 'decimal',
    minimumFractionDigits: 0,
  }).format(amount) + ' KZT';
};

const PaymentList: React.FC = () => {
  const {
    payments,
    isLoading,
    error,
    refresh,
    filterByStatus,
    search,
    statusFilter,
    searchQuery,
  } = usePayments();

  const screens = useBreakpoint();
  const isMobile = !screens.md;

  // Table columns
  const columns: ColumnsType<Payment> = [
    {
      title: 'ID транзакции',
      dataIndex: 'transaction_id',
      key: 'transaction_id',
      width: 180,
      render: (text: string) => (
        <Text code copyable>
          {text}
        </Text>
      ),
    },
    {
      title: 'Сумма',
      dataIndex: 'amount',
      key: 'amount',
      width: 150,
      render: (amount: number) => <Text strong>{formatAmount(amount)}</Text>,
    },
    {
      title: 'Аккаунт',
      dataIndex: 'account',
      key: 'account',
      width: 150,
    },
    {
      title: 'Провайдер',
      dataIndex: 'provider',
      key: 'provider',
      width: 120,
      render: (provider: string) => provider?.toUpperCase() || '-',
    },
    {
      title: 'Статус',
      dataIndex: 'status',
      key: 'status',
      width: 130,
      render: (status: PaymentStatus) => {
        const config = statusConfig[status] || statusConfig.pending;
        return (
          <Tag color={config.color} icon={config.icon}>
            {config.text}
          </Tag>
        );
      },
    },
    {
      title: 'Дата создания',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 180,
      render: (date: string | null) => formatDate(date),
    },
    {
      title: 'Комментарий',
      dataIndex: 'rgl_comment',
      key: 'rgl_comment',
      width: 200,
      render: (comment?: string, record?: Payment) => {
        if (record?.status === 'failed' && comment) {
          return (
            <Tooltip title={comment}>
              <Text type="danger" ellipsis style={{ maxWidth: 180, display: 'block' }}>
                {comment}
              </Text>
            </Tooltip>
          );
        }
        return '-';
      },
    },
  ];

  // Mobile card view
  const renderMobileCard = (payment: Payment) => (
    <Card
      key={payment.id}
      size="small"
      style={{ marginBottom: 16 }}
      title={
        <Space>
          <Text strong>{formatAmount(payment.amount)}</Text>
          {(() => {
            const config = statusConfig[payment.status] || statusConfig.pending;
            return (
              <Tag color={config.color} icon={config.icon}>
                {config.text}
              </Tag>
            );
          })()}
        </Space>
      }
    >
      <Space direction="vertical" size="small" style={{ width: '100%' }}>
        <div>
          <Text type="secondary">ID транзакции:</Text>
          <br />
          <Text code copyable>
            {payment.transaction_id}
          </Text>
        </div>
        <div>
          <Text type="secondary">Аккаунт:</Text> {payment.account}
        </div>
        <div>
          <Text type="secondary">Провайдер:</Text> {payment.provider?.toUpperCase() || '-'}
        </div>
        <div>
          <Text type="secondary">Дата создания:</Text> {formatDate(payment.created_at)}
        </div>
        {payment.status === 'failed' && payment.rgl_comment && (
          <Alert message={payment.rgl_comment} type="error" showIcon />
        )}
      </Space>
    </Card>
  );

  return (
    <div>
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        {/* Header with filters */}
        <Card>
          <Space
            direction={isMobile ? 'vertical' : 'horizontal'}
            size="middle"
            style={{ width: '100%' }}
            wrap
          >
            <Input
              placeholder="Поиск по ID или аккаунту"
              prefix={<SearchOutlined />}
              value={searchQuery}
              onChange={(e) => search(e.target.value)}
              style={{ width: isMobile ? '100%' : 300 }}
              allowClear
            />
            <Select
              value={statusFilter}
              onChange={filterByStatus}
              style={{ width: isMobile ? '100%' : 150 }}
            >
              <Select.Option value="all">Все статусы</Select.Option>
              <Select.Option value="success">Успешно</Select.Option>
              <Select.Option value="failed">Ошибка</Select.Option>
              <Select.Option value="pending">Ожидание</Select.Option>
              <Select.Option value="processing">Обработка</Select.Option>
            </Select>
            <Button
              icon={<ReloadOutlined />}
              onClick={refresh}
              loading={isLoading}
              style={{ width: isMobile ? '100%' : 'auto' }}
            >
              Обновить
            </Button>
          </Space>
        </Card>

        {/* Error message */}
        {error && (
          <Alert
            message="Ошибка загрузки"
            description={error}
            type="error"
            showIcon
            closable
          />
        )}

        {/* Desktop: Table view */}
        {!isMobile && (
          <Card>
            <Table
              columns={columns}
              dataSource={payments}
              rowKey="id"
              loading={isLoading}
              pagination={{
                pageSize: 10,
                showSizeChanger: true,
                showTotal: (total) => `Всего: ${total} платежей`,
              }}
              locale={{
                emptyText: (
                  <Empty
                    description="Нет платежей"
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                  />
                ),
              }}
              scroll={{ x: 1200 }}
            />
          </Card>
        )}

        {/* Mobile: Card view */}
        {isMobile && (
          <>
            {isLoading && <Card loading />}
            {!isLoading && payments.length === 0 && (
              <Card>
                <Empty
                  description="Нет платежей"
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                />
              </Card>
            )}
            {!isLoading && payments.map(renderMobileCard)}
          </>
        )}
      </Space>
    </div>
  );
};

export default PaymentList;
